import { useId, useState } from "react";

// Grafy Přehledu Ekonomiky (227): čárový graf s čarou „dnes" a sloupce. Kreslí se SVG
// (viewBox 0–1000, roztažené na šířku) a popisky jsou HTML nad ním — ostré na každé šířce.

export const MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
export const MONTHS_SHORT = ["led", "úno", "bře", "dub", "kvě", "čvn", "čvc", "srp", "zář", "říj", "lis", "pro"];
export const MONTHS_GEN = ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"];
export const monthGen = (ym) => MONTHS_GEN[Number(ym.slice(5, 7)) - 1];
export const monthName = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
export const monthShort = (ym) => MONTHS_SHORT[Number(ym.slice(5, 7)) - 1];
export const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

// Hezké maximum osy (1, 1,2, 1,6, 2, 2,4, 3,2, 4, 6, 8 × 10^n) — čtvrtiny pro mřížku
// vyjdou na kulatá čísla (290: dřív 15 mil. → „3,8 mil.", „11,3 mil.").
export function niceMax(v) {
  if (!(v > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const s of [1, 1.2, 1.6, 2, 2.4, 3.2, 4, 6, 8, 10]) if (v <= s * p) return s * p;
  return 10 * p;
}

// Popisek osy: „1,5 mil." / „300 tis." / „800".
export function axisLabel(v) {
  if (!v) return "0";
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toLocaleString("cs-CZ", { maximumFractionDigits: 1 })} mil.`;
  if (a >= 1e3) return `${Math.round(v / 1e3).toLocaleString("cs-CZ")} tis.`;
  return Math.round(v).toLocaleString("cs-CZ");
}

export const grid4 = (mx) => [1, 2, 3, 4].map((g) => ({ top: 100 - g * 25, label: axisLabel((mx * g) / 4) }));

// Monotónní kubická křivka (Fritsch–Carlson) — nepřestřelí mezi body.
export function monoPath(xs, ys) {
  const k = xs.length;
  if (k === 0) return "";
  if (k === 1) return `M${xs[0].toFixed(1)},${ys[0].toFixed(1)}`;
  const d = [], m = [];
  for (let i = 0; i < k - 1; i++) d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));
  m.push(d[0]);
  for (let i = 1; i < k - 1; i++) m.push(d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2);
  m.push(d[k - 2]);
  for (let i = 0; i < k - 1; i++) {
    if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / d[i], b = m[i + 1] / d[i], h = a * a + b * b;
    if (h > 9) { const t = 3 / Math.sqrt(h); m[i] = t * a * d[i]; m[i + 1] = t * b * d[i]; }
  }
  let p = `M${xs[0].toFixed(1)},${ys[0].toFixed(1)}`;
  for (let i = 0; i < k - 1; i++) {
    const dx = (xs[i + 1] - xs[i]) / 3;
    p += ` C${(xs[i] + dx).toFixed(1)},${(ys[i] + m[i] * dx).toFixed(1)} ${(xs[i + 1] - dx).toFixed(1)},${(ys[i + 1] - m[i + 1] * dx).toFixed(1)} ${xs[i + 1].toFixed(1)},${ys[i + 1].toFixed(1)}`;
  }
  return p;
}

export function TipRow({ color, value, label }) {
  return <div className="tr"><i style={{ background: color }} /><b>{value}</b><span>{label}</span></div>;
}

/**
 * Čárový graf dvou řad. `split` = index bodu „dnes": do něj plná čára a plocha,
 * od něj čárkovaně (plán). `zoneLabel` popisuje plochu za dneškem.
 * tip(i) vrací obsah bubliny pro bod i. `refs` = vodorovné čárkované čáry s popiskem
 * (cena díla, rozpočet — 290): [{ value, label, color, text }].
 */
export function LineChart({ labels, series, height = 300, split = null, zoneLabel, todayLabel = "dnes", tip, avg, endLabels = true, compact = false, refs = [] }) {
  const uid = useId().replace(/:/g, "");
  const [on, setOn] = useState(null);
  const n = labels.length;
  const all = [...series.flatMap((s) => s.values), ...refs.map((r) => r.value)];
  const mx = niceMax(Math.max(0, ...all) * 1.08);
  const X = (i) => (n > 1 ? (i / (n - 1)) * 1000 : 500);
  const Y = (v) => 1000 - (Math.max(v, 0) / mx) * 1000;
  const xs = labels.map((_, i) => X(i));
  const part = (vals, a, b) => monoPath(xs.slice(a, b + 1), vals.slice(a, b + 1).map(Y));
  const last = n - 1;
  const solidTo = split == null ? last : split;
  const ends = series.map((s) => Y(s.values[last]) / 10);
  // dva popisky na konci se nesmí překrýt
  const endTops = ends.slice();
  if (ends.length === 2 && Math.abs(ends[0] - ends[1]) < 7) {
    if (ends[1] >= ends[0]) endTops[1] = ends[0] + 7; else endTops[1] = ends[0] - 7;
  }
  return (
    <div className="ov-chart" style={{ height }}>
      <div className={`ov-plot ${compact ? "cp" : ""}`} style={compact ? { right: 58, bottom: 22 } : undefined}>
        {grid4(mx).map((g) => <div key={g.top} className="ov-gl" style={{ top: `${g.top}%` }}><span>{g.label}</span></div>)}
        {split != null && split < last && (
          <>
            <div className="ov-zone" style={{ left: `${X(split) / 10}%` }}>{zoneLabel && !compact && <span>{zoneLabel}</span>}</div>
            <div className="ov-today" style={{ left: `${X(split) / 10}%` }}><span>{todayLabel}</span></div>
          </>
        )}
        <svg className="ov-svg" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            {series.map((s, k) => (
              <linearGradient key={k} id={`${uid}g${k}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" style={{ stopColor: s.color, stopOpacity: k === 0 ? 0.3 : 0.18 }} />
                <stop offset="1" style={{ stopColor: s.color, stopOpacity: 0 }} />
              </linearGradient>
            ))}
          </defs>
          {series.map((s, k) => {
            const solid = part(s.values, 0, solidTo);
            const fut = split != null && split < last ? part(s.values, split, last) : "";
            return (
              <g key={k}>
                {solidTo > 0 && <path d={`${solid} L${X(solidTo).toFixed(1)},1000 L0,1000 Z`} fill={`url(#${uid}g${k})`} />}
                {fut && <path d={`${fut} L1000,1000 L${X(split).toFixed(1)},1000 Z`} fill={`url(#${uid}g${k})`} opacity="0.45" />}
              </g>
            );
          })}
          {series.slice().reverse().map((s, rk) => {
            const k = series.length - 1 - rk;
            const solid = part(s.values, 0, solidTo);
            const fut = split != null && split < last ? part(s.values, split, last) : "";
            return (
              <g key={`l${k}`}>
                <path className="ov-ln" d={solid} style={{ stroke: s.color }} />
                {fut && <path className="ov-ln dash" d={fut} style={{ stroke: s.color }} />}
              </g>
            );
          })}
        </svg>
        {avg && <div className="ov-avg" style={{ top: `${Y(avg.value) / 10}%` }}>{!compact && <span>{avg.label}</span>}</div>}
        {refs.map((r, k) => (
          // popisek nad čárou; když je předchozí čára těsně nad ní, pod ní (ať se nepřekryjí)
          <div key={r.label} className={`ov-ref ${k && Math.abs(Y(refs[k - 1].value) - Y(r.value)) < 90 ? "below" : ""}`}
            style={{ top: `${Y(r.value) / 10}%`, "--c": r.color, "--t": r.text || r.color }}><span>{r.label}</span></div>
        ))}
        {series.map((s, k) => <i key={k} className="ov-edot" style={{ top: `${ends[k]}%`, background: s.color }} />)}
        {endLabels && series.map((s, k) => (
          <span key={k} className={`ov-endl ${k ? "c" : ""}`} style={{ top: `${endTops[k]}%` }}>{axisLabel(s.values[last])}</span>
        ))}
        {labels.map((_, i) => (
          <div key={i} className={`ov-col ${on === i ? "on" : ""}`} style={{ left: `${X(i) / 10}%`, width: `${n > 1 ? 100 / (n - 1) : 100}%` }}
            onMouseEnter={() => setOn(i)} onMouseLeave={() => setOn(null)} onClick={() => setOn(on === i ? null : i)}>
            <i className="xh" />
            {series.map((s, k) => <i key={k} className="cd" style={{ top: `${Y(s.values[i]) / 10}%`, background: s.color }} />)}
            {tip && !compact && <div className={`ov-tip ${X(i) > 620 ? "flip" : ""}`}>{tip(i)}</div>}
          </div>
        ))}
        {tip && compact && on != null && (
          <div className="ov-tip ov-tip-fix" style={X(on) > 500 ? { left: 0 } : { left: "auto", right: 0 }}>{tip(on)}</div>
        )}
      </div>
      <div className="ov-xl" style={compact ? { right: 58 } : undefined}>
        {labels.map((l, i) => (compact && n > 9 && i % 2 ? null : <span key={i} className={i === 0 ? "first" : ""} style={{ left: `${X(i) / 10}%` }}>{l}</span>))}
      </div>
    </div>
  );
}

/**
 * Sloupcový graf. columns: [{ label, bars: [{ value, color, faded }], ghost?, tip, onClick }].
 * ghost = šedý stín celku (varianta C: stín všech nákladů, uvnitř vybraný druh).
 */
export function ColumnChart({ columns, height = 300, avg, thinLabels = false, max, compact = false }) {
  const [on, setOn] = useState(null);
  const n = columns.length;
  const top = max || niceMax(Math.max(0, ...columns.flatMap((c) => [c.ghost || 0, ...c.bars.map((b) => b.value)])) * 1.08);
  const H = (v) => `${(Math.max(v, 0) / top) * 100}%`;
  return (
    <div className="ov-chart" style={{ height }}>
      <div className="ov-plot cc" style={{ right: avg ? 64 : 0 }}>
        {grid4(top).map((g) => <div key={g.top} className="ov-gl" style={{ top: `${g.top}%` }}><span>{g.label}</span></div>)}
        {avg && <div className="ov-avg" style={{ top: `${100 - (avg.value / top) * 100}%` }}><span>{avg.label}</span></div>}
        <div className="ov-bars">
          {columns.map((c, i) => {
            const single = c.bars.length === 1;
            return (
              <div key={i} className={`ov-bg ${c.onClick ? "click" : ""} ${on === i ? "on" : ""}`} style={{ "--i": i }}
                onMouseEnter={() => setOn(i)} onMouseLeave={() => setOn(null)}
                onClick={c.onClick || (() => setOn(on === i ? null : i))}
                role={c.onClick ? "button" : undefined} tabIndex={c.onClick ? 0 : undefined}
                onKeyDown={c.onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); c.onClick(); } } : undefined}
                aria-label={c.aria}>
                {c.ghost != null ? (
                  <span className="ov-stack">
                    <i className="gh" style={{ height: H(c.ghost) }} />
                    {c.bars.map((b, k) => <i key={k} className="ov-bar" style={{ height: H(b.value), background: b.color }} />)}
                  </span>
                ) : c.bars.map((b, k) => (
                  <i key={k} className={`ov-bar ${single ? "one" : ""} ${b.faded ? "fu" : ""}`} style={{ height: H(b.value), background: b.color }} />
                ))}
                {c.tip && !compact && <div className={`ov-tip ${i >= n * (n > 6 ? 0.5 : 0.45) ? "flip" : ""}`}>{c.tip}</div>}
              </div>
            );
          })}
        </div>
        {compact && on != null && columns[on]?.tip && (
          <div className="ov-tip ov-tip-fix" style={on >= n / 2 ? { left: 0 } : { left: "auto", right: 0 }}>{columns[on].tip}</div>
        )}
      </div>
      <div className={`ov-xb ${thinLabels ? "thin" : ""}`} style={{ right: avg ? 64 : 0 }}>
        {columns.map((c, i) => <span key={i} title={c.title || c.label}>{c.label}</span>)}
      </div>
    </div>
  );
}

export function Legend({ items }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] font-medium text-muted-foreground">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-2">
          {it.dash ? <i className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: it.color }} />
            : it.line ? <i className="inline-block w-4 h-[3px] rounded" style={{ background: it.color }} />
              : <i className="inline-block w-2.5 h-2.5 rounded-[3px]" style={{ background: it.color }} />}
          {it.label}
        </span>
      ))}
    </div>
  );
}
